/**
 * Public API for the universal evidence-block rendering used on every
 * research detail page (vaccine, ingredient, disease, manufacturer,
 * surveillance system, future explorers).
 *
 * Renders a single evidence_block from the backend with:
 *   • Evidence label chip (Official guidance / Regulatory / Peer-reviewed /
 *     Safety surveillance / Historical / TCP investigation)
 *   • Evidence grade chip when present (Established / Conflicting /
 *     Ongoing research / Hypothesis)
 *   • Markdown body
 *   • Citation list with links
 *   • Save button (granular section-level save with parent_ref)
 */
import React from "react";
import { Tag } from "lucide-react";
import SaveButton from "./SaveButton";
import { renderMarkdown } from "../../lib/markdown";

const LABEL_DISPLAY = {
    official_guidance: { label: "Official guidance", className: "bg-[#1f4a2d] text-[#7FE0A6]" },
    regulatory_document: { label: "Regulatory", className: "bg-[#1f3a4a] text-[#56CCF2]" },
    peer_reviewed: { label: "Peer-reviewed", className: "bg-[#2a1f3a] text-[#A78BFA]" },
    safety_surveillance: { label: "Safety surveillance", className: "bg-[#4a2b1a] text-[#E0B341]" },
    historical: { label: "Historical", className: "bg-[#3a3a3a] text-[#94A3B8]" },
    tcp_investigation: { label: "TCP investigation", className: "bg-[#1e3a5f] text-[#56CCF2]" },
};

const GRADE_DISPLAY = {
    established: { label: "Established evidence", className: "bg-[#1f4a2d] text-[#7FE0A6]", help: "Multiple high-quality studies converge on this finding." },
    conflicting: { label: "Conflicting evidence", className: "bg-[#4a2b1a] text-[#E0B341]", help: "High-quality studies disagree." },
    ongoing_research: { label: "Ongoing research", className: "bg-[#1f3a4a] text-[#56CCF2]", help: "Active area of investigation, no consensus yet." },
    hypothesis: { label: "Hypothesis", className: "bg-[#3a1a1a] text-[#F87171]", help: "Proposed but not yet meaningfully tested." },
};

export default function EvidenceBlock({ block, parentRef = null, vaccineSlug = null }) {
    const label = LABEL_DISPLAY[block.evidence_label] || { label: block.evidence_label, className: "bg-[#243044] text-[#94A3B8]" };
    const grade = block.evidence_grade ? GRADE_DISPLAY[block.evidence_grade] : null;
    const blockUrl = vaccineSlug ? `/vaccines/v/${vaccineSlug}#evidence-${block.id}` : undefined;

    return (
        <article
            id={`evidence-${block.id}`}
            className="scroll-mt-24 bg-[#111827] border border-[#243044] rounded-xl p-6"
            data-testid={`evidence-block-${block.id}`}
        >
            <header className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${label.className}`}>
                            {label.label}
                        </span>
                        {grade ? (
                            <span title={grade.help} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${grade.className}`}>
                                <Tag className="w-3 h-3" />
                                {grade.label}
                            </span>
                        ) : null}
                    </div>
                    <h3 className="text-lg font-semibold text-white tracking-tight">{block.title}</h3>
                </div>
                <SaveButton
                    kind={block.block_type === "research_area" ? "research_area" : "evidence_block"}
                    refSlug={block.id}
                    refTitle={block.title}
                    parentRef={parentRef}
                    fragment={`#evidence-${block.id}`}
                    refUrl={blockUrl}
                    variant="icon"
                />
            </header>
            {block.body_md ? (
                <div className="prose prose-invert prose-sm max-w-none text-[#CBD5E1] mt-3" dangerouslySetInnerHTML={{ __html: renderMarkdown(block.body_md) }} />
            ) : null}
            {block.citations && block.citations.length > 0 ? (
                <div className="mt-4 pt-3 border-t border-[#243044]">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-1.5">Citations</div>
                    <ul className="space-y-1">
                        {block.citations.map((c, i) => (
                            <li key={i} className="text-[12px]">
                                {c.url ? (
                                    <a href={c.url} target="_blank" rel="noreferrer" className="text-[#56CCF2] hover:text-white transition-colors">{c.label || c.url}</a>
                                ) : (
                                    <span className="text-[#94A3B8]">{c.label}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </article>
    );
}
